import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import FullTimelineModal from './FullTimelineModal';
import CardDetailsModal from './CardDetailsModal';
import './scss/BoardAnalyticsDashboard.scss';

// Redux imports
import {
  fetchBoardMetrics,
  fetchBoardTimeAnalytics,
  fetchBoardFullTimeline,
  fetchBoardUserProductivity,
  fetchBoardWorkflowAnalytics,
  setAnalyticsParams,
  selectAnalyticsLoadingState,
  fetchBoardAnalytics
} from '../../redux/Slices/analyticsSlice';
import { useDispatch, useSelector } from 'react-redux';
import Monitoring from '../icons/Monitoring';
import MoreHoriz from '../icons/MoreHoriz';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import BouncingDots from '../Global/BouncingDots';
import AccessDenied from '../AccessDenied';
import NotFound from '../400';

// Custom Tooltip Component with proper theme colors
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="tooltip-value" style={{ color: 'var(--primary-text-color)' }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const BoardAnalyticsDashboard = () => {

  const dispatch = useDispatch();

  // Redux state selectors
  const { metrics, timeAnalytics, userProductivity, workflowAnalytics, boardAnalytics } = useSelector((state) => state.analytics);
  const loadingState = useSelector(selectAnalyticsLoadingState);
  const analysis = useSelector((state) => state.analytics);

  // Extract data from Redux state
  const metricsData = metrics?.data;
  const timeAnalyticsData = timeAnalytics?.data;
  const userProductivityRawData = userProductivity?.data;
  const workflowAnalyticsData = workflowAnalytics?.data;
  const boardAnalyticsData = boardAnalytics?.data;

  // Extract specific data fields
  const aiInsights = boardAnalyticsData?.aiInsights || [];
  const performanceScore = boardAnalyticsData?.performanceScore || null;
  const generatedAt = boardAnalyticsData?.generatedAt || new Date().toISOString();


  const { boardId } = useParams();
  const [dateRange, setDateRange] = useState(30);
  const [showFullTimeline, setShowFullTimeline] = useState(false);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [cardDetailsType, setCardDetailsType] = useState('');
  const [cardDetailsTitle, setCardDetailsTitle] = useState('');
  const [accessError, setAccessError] = useState(null);


  // Initialize analytics parameters
  useEffect(() => {
    if (boardId) {
      dispatch(setAnalyticsParams({ boardId, dateRange }));
    }
  }, [dispatch, boardId, dateRange]);

  // Fetch analytics data independently and asynchronously
  useEffect(() => {
    if (boardId) {
      const fetchParams = { boardId, dateRange };

      // Fetch each component independently for better performance
      const fetchData = async () => {
        try {
          setAccessError(null);
          await Promise.all([
            dispatch(fetchBoardAnalytics(fetchParams)).unwrap(),
            dispatch(fetchBoardMetrics(fetchParams)).unwrap(),
            dispatch(fetchBoardTimeAnalytics(fetchParams)).unwrap(),
            dispatch(fetchBoardUserProductivity(fetchParams)).unwrap(),
            dispatch(fetchBoardWorkflowAnalytics(fetchParams)).unwrap()
          ]);
        } catch (error) {
          // Handle access denied errors
          if (error?.type === 'ACCESS_DENIED' || error?.status === 403) {
            setAccessError({
              type: 'ACCESS_DENIED',
              message: error?.message || 'You do not have access to this board analytics'
            });
          } else if (error?.type === 'NOT_FOUND' || error?.status === 404) {
            setAccessError({
              type: 'NOT_FOUND',
              message: error?.message || 'Board not found'
            });
          }
          // For other errors, we'll let individual components handle them
        }
      };

      fetchData();
    }
  }, [dispatch, boardId, dateRange]);

  // Handle date range change
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  // Handle refresh - refetch all data
  const handleRefresh = () => {
    if (boardId) {
      const fetchParams = { boardId, dateRange };
      dispatch(fetchBoardAnalytics(fetchParams));
      dispatch(fetchBoardMetrics(fetchParams));
      dispatch(fetchBoardTimeAnalytics(fetchParams));
      dispatch(fetchBoardUserProductivity(fetchParams));
      dispatch(fetchBoardWorkflowAnalytics(fetchParams));
    }
  };

  // Handle opening card details modal
  const handleOpenCardDetails = (type, title) => {
    setCardDetailsType(type);
    setCardDetailsTitle(title);
    setShowCardDetails(true);
  };


  // Process data for timeline chart
  const processTimelineData = () => {
    const timeAnalytics = timeAnalyticsData?.timeAnalytics;
    if (!timeAnalytics) return [];

    // Use daily timeline data if available, otherwise fall back to weekly
    if (timeAnalytics.dailyTimeline && timeAnalytics.dailyTimeline.length > 0) {
      // For daily view, limit the number of points based on date range
      const maxPoints = dateRange <= 7 ? dateRange : dateRange <= 30 ? 30 : 30;
      return timeAnalytics.dailyTimeline
        .slice(-maxPoints)
        .map(day => ({
          date: day.date,
          displayDate: day.displayDate,
          dayOfWeek: day.dayOfWeek,
          dayOfMonth: day.dayOfMonth,
          completed: day.completed,
          created: day.created,
          total: (day.completed) + (day.created),
          percentage: day.percentage
        }));
    }

    // Fallback to weekly data for backward compatibility
    if (timeAnalytics.weekly) {
      return Object.entries(timeAnalytics.weekly)
        .sort(([a], [b]) => new Date(a) - new Date(b))
        .slice(-6)
        .map(([, values], index) => ({
          week: `Week ${index + 1}`,
          completed: values.completed,
          total: (values.completed) + (values.created),
          percentage: values.created > 0 ? ((values.completed / values.created) * 100) : 0
        }));
    }

    return [];
  };

  // Calculate percentage changes from comparison data
  const getPercentageChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Get recent activities from user productivity data
  const getRecentActivities = () => {
    const userProductivityArray = userProductivityRawData?.userProductivity || [];
    if (!Array.isArray(userProductivityArray)) return [];

    return userProductivityArray
      ?.filter(user => user.cardsCompleted > 0)
      ?.slice(0, 3)
      ?.map((user) => ({
        name: user.name || user.username,
        profilePicture: user.profilePicture,
        action: `Completed ${user.cardsCompleted} card${user.cardsCompleted > 1 ? 's' : ''}`,
        project: userProductivityRawData.boardTitle,
        time: getTimeAgo(generatedAt),
        avatar: user.name ? user.name.charAt(0).toUpperCase() : 'U'
      }));
  };

  // Get time ago helper
  const getTimeAgo = (date) => {
    if (!date) return 'Recently';
    const now = new Date();
    const past = new Date(date);
    const diffInMinutes = Math.floor((now - past) / (1000 * 60));

    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Get user productivity data for meaningful analytics
  const getUserProductivityData = () => {
    const userProductivityArray = userProductivityRawData?.userProductivity || [];
    if (!Array.isArray(userProductivityArray)) return [];

    return userProductivityArray.map((user) => ({
      id: user.userId || user._id, // Handle both new and legacy data structures
      name: user.name || user.username,
      username: user.username,
      profilePicture: user.profilePicture,
      cardsAssigned: user.cardsAssigned,
      cardsCompleted: user.cardsCompleted,
      completionRate: user.completionRate,
      productivity: user.productivity,
      avgCompletionTime: user.avgCompletionTime,
      status: user.completionRate > 80 ? 'high' : user.completionRate > 50 ? 'normal' : 'low'
    }));
  };

  const getPriorityDistributionData = () => {
    const workflowAnalytics = workflowAnalyticsData?.workflowAnalytics;
    if (!workflowAnalytics?.priorityDistribution) return [];

    const distribution = workflowAnalytics.priorityDistribution;

    const priorityOrder = ['Urgent', 'High', 'Normal', 'Low'];
    const colors = ['#ff8589', '#ffc53d', '#3e63dd', '#606060'];

    return priorityOrder
      .map((priority, index) => {
        const value = distribution[priority];
        if (value <= 0) return null; // skip if no cards
        const color = colors[index];
        return {
          name: priority,
          value,
          color,
        };
      })
      .filter(Boolean);
  };

  // Get list distribution data for bar chart
  const getListDistributionData = () => {
    const workflowAnalytics = workflowAnalyticsData?.workflowAnalytics;
    if (!workflowAnalytics?.listDistribution) return [];

    return Object.entries(workflowAnalytics.listDistribution)
      .filter(([, value]) => value > 0)
      .map(([name, value]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        fullName: name,
        value: value
      })); // Show top 6 lists
  };

  // Get AI insights summary
  const getAIInsightsSummary = () => {
    if (!aiInsights || aiInsights.length === 0) return null;

    const highPriorityInsights = aiInsights.filter(insight =>
      insight.severity === 'high' || insight.severity === 'critical'
    );

    return {
      total: aiInsights.length,
      highPriority: highPriorityInsights.length,
      latest: aiInsights[0]
    };
  };

  // Check if any data is loading
  const isLoading = loadingState.isAnyLoading;

  // Check for errors
  const hasError = metrics?.error || timeAnalytics?.error || userProductivity?.error || workflowAnalytics?.error;



  const timelineData = processTimelineData();
  const recentActivities = getRecentActivities();
  const userProductivityData = getUserProductivityData();
  const priorityDistributionData = getPriorityDistributionData();
  const listDistributionData = getListDistributionData();
  const aiInsightsSummary = getAIInsightsSummary();
  const comparison = metricsData?.metrics?.comparison || metricsData?.comparison || {};

  // Calculate real metrics
  const totalProjects = metricsData?.metrics?.totalCards || metricsData?.totalCards;
  const cardsCompleted = metricsData?.metrics?.completedCards || metricsData?.completedCards;
  const teamUtilization = metricsData?.metrics?.completionRate || metricsData?.completionRate;
  const upcomingDeadlines = metricsData?.metrics?.overdueCards || metricsData?.overdueCards;

  // Calculate trend changes
  const projectsChange = getPercentageChange(totalProjects, comparison.previousPeriod?.totalCards);
  const completedChange = getPercentageChange(cardsCompleted, comparison.previousPeriod?.completedCards);
  const utilizationChange = getPercentageChange(teamUtilization, comparison.previousPeriod?.completionRate);
  const deadlinesChange = getPercentageChange(upcomingDeadlines, comparison.previousPeriod?.overdueCards);

  // Access error handling
  if (accessError) {
    if (accessError.type === 'ACCESS_DENIED') {
      return <AccessDenied message={accessError.message} type="board" />;
    } else if (accessError.type === 'NOT_FOUND') {
      return <NotFound />;
    }
  }

  return (
    <div className="reports-dashboard">
      {/* Header Section */}
      <div className="reports-header">

        <div className="header-controls">
          <div className="time-selector">
            <span className="material-symbols-outlined">calendar_month</span>
            <select
              value={dateRange}
              onChange={(e) => handleDateRangeChange(parseInt(e.target.value))}
              className="time-dropdown"
            >
              <option value={7}>This Week</option>
              <option value={30}>This Month</option>
              <option value={90}>This Quarter</option>
            </select>
            <span className="material-symbols-outlined">expand_more</span>
          </div>

          <button className="share-button">
            <span className="material-symbols-outlined">share</span>
            Share
          </button>

          <button
            onClick={handleRefresh}
            className="export-button"
            disabled={isLoading}
          >
            <span className="material-symbols-outlined">
              {isLoading ? 'hourglass_top' : 'download'}
            </span>
            Export
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="reports-content">
        {/* Summary Cards */}
        <div className="summary-cards">
          <div
            className="summary-card clickable"
            onClick={() => handleOpenCardDetails('total', 'All Cards')}
          >
            <div className="card-header">
              <div className="card-info">
                <h3>Total Cards</h3>
                <div className="card-value">{loadingState.metrics && !metricsData ? <BouncingDots size={5} /> : totalProjects}</div>
              </div>
              <div className="card-icon">
                <span className="material-symbols-outlined">assignment</span>
              </div>
            </div>
            <div className="card-footer">
              <span className="trend-text">From Previous Period</span>
              <span className={`trend-indicator ${projectsChange >= 0 ? 'positive' : 'negative'}`}>
                <span className="material-symbols-outlined">
                  {projectsChange >= 0 ? 'trending_up' : 'trending_down'}
                </span>
                {projectsChange >= 0 ? '+' : ''}{projectsChange}%
              </span>
            </div>
          </div>

          <div
            className="summary-card clickable"
            onClick={() => handleOpenCardDetails('completed', 'Completed Cards')}
          >
            <div className="card-header">
              <div className="card-info">
                <h3>Cards Completed</h3>
                <div className="card-value">{loadingState.metrics && !metricsData ? <BouncingDots size={5} /> : cardsCompleted}</div>
              </div>
              <div className="card-icon">
                <span className="material-symbols-outlined">
                  task_alt
                </span>
              </div>
            </div>
            <div className="card-footer">
              <span className="trend-text">From Previous Period</span>
              <span className={`trend-indicator ${completedChange >= 0 ? 'positive' : 'negative'}`}>
                <span className="material-symbols-outlined">
                  {completedChange >= 0 ? 'trending_up' : 'trending_down'}
                </span>
                {completedChange >= 0 ? '+' : ''}{completedChange}%
              </span>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-header">
              <div className="card-info">
                <h3>Completion Rate</h3>
                <div className="card-value">{loadingState.metrics && !metricsData ? <BouncingDots size={5} /> : <>{Number(teamUtilization)?.toFixed(1)}0%</>}</div>
              </div>
              <div className="card-icon">
                <span className="material-symbols-outlined">percent</span>
              </div>
            </div>
            <div className="card-footer">
              <span className="trend-text">From Previous Period</span>
              <span className={`trend-indicator ${utilizationChange >= 0 ? 'positive' : 'negative'}`}>
                <span className="material-symbols-outlined">
                  {utilizationChange >= 0 ? 'trending_up' : 'trending_down'}
                </span>
                {utilizationChange >= 0 ? '+' : ''}{utilizationChange}%
              </span>
            </div>
          </div>

          <div
            className="summary-card clickable"
            onClick={() => handleOpenCardDetails('overdue', 'Overdue Cards')}
          >
            <div className="card-header">
              <div className="card-info">
                <h3>Overdue Cards</h3>
                <div className="card-value">{loadingState.metrics && !metricsData ? <BouncingDots size={5} /> : upcomingDeadlines}</div>
              </div>
              <div className="card-icon">
                <span className="material-symbols-outlined">calendar_month</span>

              </div>
            </div>
            <div className="card-footer">
              <span className="trend-text">From Previous Period</span>
              <span className={`trend-indicator ${deadlinesChange <= 0 ? 'positive' : 'negative'}`}>
                <span className="material-symbols-outlined">
                  {deadlinesChange <= 0 ? 'trending_down' : 'trending_up'}
                </span>
                {deadlinesChange >= 0 ? '+' : ''}{deadlinesChange}%
              </span>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="main-content-grid">
          {/* Project Timeline */}
          <div className="timeline-section">
            <div className="section-header">
              <div className="timeline-title">
                <h3>Completion Timeline</h3>

                {loadingState.timeAnalytics && !timeAnalyticsData ? (
                  <div className="section-loading">
                    <BouncingDots size={6} />
                  </div>
                ) : (
                  <span className="timeline-indicator">
                    <span className="indicator-dot"></span>
                    {timeAnalyticsData?.timeAnalytics?.trends?.direction === 'up' ? '+' : timeAnalyticsData?.timeAnalytics?.trends?.direction === 'down' ? '-' : ''}
                    {timeAnalyticsData?.timeAnalytics?.trends?.percentage}% {timeAnalyticsData?.timeAnalytics?.trends?.description}
                  </span>
                )}
              </div>
              <div className="timeline-controls">
                <button
                  className="view-full-timeline"
                  onClick={() => setShowFullTimeline(true)}
                >
                  View Full Timeline
                </button>
                <button className="view-full-analytics">
                  <Monitoring color={'var(--primary-text-color)'} size={18} />
                </button>
              </div>
            </div>


            <div className="timeline-chart">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={timelineData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="completedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--brand-color)" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="var(--brand-color)" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--outline-color)" opacity={0.2} />
                  <XAxis
                    dataKey={timelineData.length > 0 && timelineData[0].displayDate ? "displayDate" : "week"}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'var(--primary-text-color)', fontSize: 12, opacity: 0.7 }}
                    interval={dateRange <= 7 ? 0 : dateRange <= 30 ? Math.ceil(timelineData.length / 8) : Math.ceil(timelineData.length / 6)}
                  />
                  <YAxis hide />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="completed"
                    stroke="var(--brand-color)"
                    strokeWidth={2}
                    fill="url(#completedGradient)"
                    dot={dateRange <= 7 ? { fill: 'var(--brand-color)', strokeWidth: 2, r: 4 } : false}
                    activeDot={{ r: 6, fill: 'var(--brand-color)', stroke: 'var(--single-card-background-color)', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {timelineData.length > 0 && (() => {
                // Calculate total completed cards across all timeline data
                const totalCompleted = timelineData.reduce((sum, day) => sum + (day.completed), 0);

                return (
                  <div className="timeline-milestone">
                    <div className="milestone-marker">
                      <div className="milestone-line"></div>
                      <div className="milestone-point">
                        <div className="milestone-tooltip">
                          <span className="weak-text">
                            Total Progress
                          </span>
                          <span className="circle-text">
                            <span className="circle-done">●</span>
                            Completed: {totalCompleted}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

            </div>
          </div>

          {/* Recent Activity */}
          <div className="activity-section">
            <div className="section-header">
              <h3>Recent Activity</h3>
              <button className="more-options">
                <MoreHoriz size={20} />
              </button>
            </div>


            {loadingState.userProductivity && !userProductivityRawData ? (
              <div className="section-loading">
                <BouncingDots size={5} />
              </div>
            ) : (
              <>
                <div className="activity-list">
                  {recentActivities.length > 0 ? recentActivities.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">
                        {activity.profilePicture ? (
                          <img src={activity.profilePicture} alt={activity.name} loading="lazy" />
                        ) : (
                          <ImagePlaceholder key={activity.id} size={40} text={activity.name} onlineIndicator={false} />
                        )}
                      </div>
                      <div className="activity-content">
                        <div className="activity-header">
                          <span className="activity-name">{activity.name}</span>
                          <span className="activity-project">{activity.project}</span>
                        </div>
                        <div className="activity-action">{activity.action}</div>
                      </div>
                      <div className="activity-time">{activity.time}</div>
                    </div>
                  )) : (
                    <div className="no-activity">
                      <span className="material-symbols-outlined">history</span>
                      <p>No recent activity data available</p>
                      <small>Activity will appear here when team members complete cards</small>
                    </div>
                  )}
                </div>
                <button className="see-all-activity">See All Activity</button>
              </>
            )}

          </div>
        </div>

        {/* User Productivity Analytics */}
        <div className="user-productivity-section">
          <div className="section-header">
            <h3>User Productivity Analytics</h3>
            <p className="section-subtitle">Team member performance and contribution metrics</p>
          </div>

          <div className="productivity-grid">
            {/* User Productivity Table */}
            <div className="productivity-table-card">
              <h4>Team Performance</h4>
              <div className="productivity-table">
                {loadingState.userProductivity && !userProductivityRawData ? (
                  <div className="section-loading">
                    <BouncingDots size={5} />
                  </div>
                ) : userProductivityData.length > 0 ? (
                  <div className="table-container">
                    {userProductivityData.map((user, index) => (
                      <div key={user.id || index} className="user-row">
                        <div className="user-info">
                          <div className="user-avatar">
                            {user.profilePicture ? (
                              <img src={user.profilePicture} alt={user.name} loading="lazy" />
                            ) : (
                              <ImagePlaceholder key={user.id} size={40} text={user.username} onlineIndicator={false} />
                            )}
                          </div>
                          <div className="user-details">
                            <div className="user-name">{user.name || user.username}</div>
                            <div className="user-username">@{user.username}</div>
                          </div>
                        </div>
                        <div className="user-metrics">
                          <div className="metric">
                            <span className="metric-label">Assigned</span>
                            <span className="metric-value">{user.cardsAssigned}</span>
                          </div>
                          <div className="metric">
                            <span className="metric-label">Completed</span>
                            <span className="metric-value">{user.cardsCompleted}</span>
                          </div>
                          <div className="metric">
                            <span className="metric-label">Rate</span>
                            <span className="metric-value">{Number(user.completionRate)?.toFixed(1)}%</span>
                          </div>
                          <div className="metric">
                            <span className="metric-label">Avg Time</span>
                            <span className="metric-value">{Number(user.avgCompletionTime)?.toFixed(1)}d</span>
                          </div>
                        </div>
                        <div className="user-status">
                          <span className={`status-indicator ${user.status}`}>
                            {user.status === 'high' ? 'High Performer' :
                              user.status === 'normal' ? 'Good Performer' : 'Needs Support'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-data">
                    <span className="material-symbols-outlined">people</span>
                    <p>No user productivity data available</p>
                    <small>Data will appear here when team members are active</small>
                  </div>
                )}
              </div>
            </div>

            {/* Priority Distribution Chart */}
            <div className="priority-chart-card">
              <h4>Card Priority Distribution</h4>
              {loadingState.workflowAnalytics && !workflowAnalyticsData ? (
                <div className="section-loading">
                  <BouncingDots size={5} />
                </div>
              ) : priorityDistributionData.length > 0 ? (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={priorityDistributionData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {priorityDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="chart-legend">
                    {priorityDistributionData.map((item, index) => (
                      <div key={index} className="legend-item">
                        <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                        <span className="legend-label">{item.name}</span>
                        <span className="legend-value">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="no-data">
                  <span className="material-symbols-outlined">pie_chart</span>
                  <p>No priority data available</p>
                  <small>Priority distribution will appear here</small>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Workflow Analytics Section */}
        <div className="workflow-analytics-section">
          <div className="section-header">
            <h3>Workflow Analytics</h3>
            <p className="section-subtitle">Card distribution and workflow insights</p>
          </div>

          <div className="workflow-grid">
            {/* List Distribution Chart */}
            <div className="workflow-card">
              <h4>List Distribution</h4>
              {loadingState.workflowAnalytics && !workflowAnalyticsData ? (
                <div className="section-loading">
                  <BouncingDots size={5} />
                </div>
              ) : listDistributionData.length > 0 ? (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={listDistributionData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--outline-color)" opacity={0.2} />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'var(--primary-text-color)', fontSize: 11, opacity: 0.7 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'var(--primary-text-color)', fontSize: 11, opacity: 0.7 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="value"
                        fill="var(--brand-color)"
                        radius={[4, 4, 0, 0]}
                        opacity={0.8}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="no-data">
                  <span className="material-symbols-outlined">bar_chart</span>
                  <p>No list distribution data</p>
                  <small>Data will appear when lists contain cards</small>
                </div>
              )}
            </div>

            {/* Bottlenecks */}
            {workflowAnalyticsData?.workflowAnalytics?.bottlenecks && workflowAnalyticsData.workflowAnalytics.bottlenecks.length > 0 && (
              <div className="workflow-card">
                <h4>Identified Bottlenecks</h4>
                <div className="bottlenecks-list">
                  {workflowAnalyticsData.workflowAnalytics.bottlenecks.slice(0, 3).map((bottleneck, index) => (
                    <div key={index} className={`bottleneck-item ${bottleneck.severity}`}>
                      <div className="bottleneck-header">
                        <span className="bottleneck-list">{bottleneck.list}</span>
                        <span className={`severity-badge ${bottleneck.severity}`}>
                          {bottleneck.severity}
                        </span>
                      </div>
                      <div className="bottleneck-count">{bottleneck.cardCount} cards</div>
                      <div className="bottleneck-suggestion">{bottleneck.suggestion}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Insights Section */}
        {aiInsights && aiInsights.length > 0 && (
          <div className="ai-insights-section">
            <div className="section-header">
              <h3>AI Insights & Recommendations</h3>
              <div className="insights-summary">
                <span className="insights-count">
                  {aiInsights.length} insight{aiInsights.length > 1 ? 's' : ''}
                </span>
                {aiInsightsSummary?.highPriority > 0 && (
                  <span className="high-priority-badge">
                    {aiInsightsSummary.highPriority} high priority
                  </span>
                )}
              </div>
            </div>

            <div className="insights-grid">
              {aiInsights.slice(0, 3).map((insight, index) => (
                <div key={index} className={`insight-card ${insight.severity}`}>
                  <div className="insight-header">
                    <div className="insight-type">
                      <span className="material-symbols-outlined">psychology</span>
                      <span>{insight.type}</span>
                    </div>
                    <span className={`insight-severity ${insight.severity}`}>
                      {insight.severity}
                    </span>
                  </div>
                  <h4>{insight.title}</h4>
                  <p>{insight.description}</p>
                  {insight.recommendations && insight.recommendations.length > 0 && (
                    <div className="insight-recommendations">
                      <strong>Recommendations:</strong>
                      <ul>
                        {insight.recommendations.slice(0, 2).map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="insight-confidence">
                    Confidence: {Math.round((insight.confidence) * 100)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance Score Section */}
        {performanceScore && (
          <div className="performance-score-section">
            <div className="section-header">
              <h3>Performance Metrics</h3>
              <p className="section-subtitle">Overall board performance indicators</p>
            </div>

            <div className="performance-cards">
              <div className="performance-card">
                <div className="performance-header">
                  <h4>Overall Score</h4>
                  <div className="score-value">{performanceScore.overall}%</div>
                </div>
                <div className="performance-bar">
                  <div
                    className="performance-fill overall"
                    style={{ width: `${performanceScore.overall}%` }}
                  ></div>
                </div>
              </div>

              <div className="performance-card">
                <div className="performance-header">
                  <h4>Productivity</h4>
                  <div className="score-value">{performanceScore.productivity}%</div>
                </div>
                <div className="performance-bar">
                  <div
                    className="performance-fill productivity"
                    style={{ width: `${performanceScore.productivity}%` }}
                  ></div>
                </div>
              </div>

              <div className="performance-card">
                <div className="performance-header">
                  <h4>Efficiency</h4>
                  <div className="score-value">{performanceScore.efficiency}%</div>
                </div>
                <div className="performance-bar">
                  <div
                    className="performance-fill efficiency"
                    style={{ width: `${performanceScore.efficiency}%` }}
                  ></div>
                </div>
              </div>

              <div className="performance-card">
                <div className="performance-header">
                  <h4>Collaboration</h4>
                  <div className="score-value">{performanceScore.collaboration}%</div>
                </div>
                <div className="performance-bar">
                  <div
                    className="performance-fill collaboration"
                    style={{ width: `${performanceScore.collaboration}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>

      {/* Full Timeline Modal */}
      <FullTimelineModal
        isOpen={showFullTimeline}
        onClose={() => setShowFullTimeline(false)}
        boardId={boardId}
      />

      {/* Card Details Modal */}
      <CardDetailsModal
        isOpen={showCardDetails}
        onClose={() => setShowCardDetails(false)}
        boardId={boardId}
        cardType={cardDetailsType}
        title={cardDetailsTitle}
        totalCards={totalProjects}
      />
    </div>
  );
};

export default BoardAnalyticsDashboard;
